import React, { Fragment, useCallback, useEffect, useState, useRef, memo } from 'react';
import { splitEvery } from 'ramda';
import Slider from 'js/components/common/Slider';

import { Game } from '../Game';
import * as helpers from './helpers';
import { SLIDER_OPTIONS_MINI, GAMES_PER_SLIDE_MIN } from './constants';

import style from './GridPagingSlider.scss';

// Duplicate of GoldenChipsBalances components.
// Since we need to use it for all accordion tables - these components moved here.

export const GridPagingSlider = memo(({ gameCodes }: { gameCodes: string[] }) => {
    const frame = useRef<number>(0);
    const gameRef = useRef<HTMLDivElement | undefined>();
    const containerRef = useRef<HTMLDivElement | undefined>();
    const getSlides = useCallback((perSlide: number) => gameCodes.length ? splitEvery(perSlide, gameCodes) : [], [gameCodes]);
    const [slides, setSlides] = useState(() => getSlides(GAMES_PER_SLIDE_MIN));

    const updateSlides = useCallback(() => {
        if (!containerRef.current || !gameRef.current) {
            return;
        }

        const totalGamesCount = helpers.getTotalGamesCount(containerRef.current, gameRef.current);

        if (slides[0].length !== totalGamesCount && gameCodes.length !== slides[0].length) {
            setSlides(getSlides(totalGamesCount));
        }
    }, [containerRef, gameRef, setSlides, slides, getSlides, gameCodes.length]);

    const setSlideRef = (ref: HTMLDivElement) => {
        containerRef.current = ref;
        updateSlides();
    };

    const setGameRef = (ref: HTMLDivElement) => {
        gameRef.current = ref;
        updateSlides();
    };

    const throttledCalculatedGamePerSlide = useCallback(() => {
        cancelAnimationFrame(frame.current);
        frame.current = requestAnimationFrame(updateSlides);
    }, [updateSlides]);

    useEffect(() => {
        window.addEventListener('resize', throttledCalculatedGamePerSlide);

        return () => {
            window.removeEventListener('resize', throttledCalculatedGamePerSlide);
        };
    }, [throttledCalculatedGamePerSlide]);

    if (!slides.length) {
        return null;
    }

    return (
        <Slider
            swipe={false}
            rotation={false}
            renderAll={false}
            showControls={true}
            isAllItemsVisible={true}
            showControlsOnHover={false}
            options={SLIDER_OPTIONS_MINI}
            refs={{ slide: setSlideRef }}
            className={style.sliderWrapper}
            classNames={ { ...style, slider: style.innerSlider }}>
            {slides.map((slide, i) => (
                <Fragment key={`${slide.length}_${i}_${slides[0][0]}`}>
                    {slide.map((code, index) => (
                        <Game
                            key={code}
                            code={code}
                            className={style.game}
                            {...(i === 0 && index === 0 && { setGameRef })}
                        />
                    ))}
                </Fragment>
            ))}
        </Slider>
    );
});
